/**
 * KeyboardShortcutsHelp Component
 * 
 * Documentation for keyboard shortcuts:
 * - Displays available keyboard shortcuts
 * - Accessible modal dialog
 * - Can be toggled with ? key
 * 
 * Accessibility requirement: Keyboard shortcuts documentation
 */

import React, { useEffect, useState } from 'react';
import './KeyboardShortcutsHelp.css';

export interface KeyboardShortcutsHelpProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface Shortcut {
  key: string;
  description: string;
  category: string;
}

const SHORTCUTS: Shortcut[] = [
  { key: '⌘K', description: 'Global search', category: 'Search' },
  { key: '?', description: 'Show/hide this help', category: 'Help' },
  { key: 'Esc', description: 'Close dialogs', category: 'Help' },
  { key: '+', description: 'Zoom in', category: 'Navigation' },
  { key: '-', description: 'Zoom out', category: 'Navigation' },
  { key: '←', description: 'Pan left', category: 'Navigation' },
  { key: '→', description: 'Pan right', category: 'Navigation' },
  { key: 'R', description: 'Reset zoom', category: 'Navigation' },
  { key: 'E', description: 'Export chart', category: 'Actions' },
  { key: 'Tab', description: 'Navigate between controls', category: 'Navigation' },
  { key: 'Enter', description: 'Activate focused control', category: 'Actions' },
  { key: 'Space', description: 'Toggle focused control', category: 'Actions' },
];

/**
 * KeyboardShortcutsHelp component
 * 
 * Displays keyboard shortcuts documentation in accessible modal
 */
export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  isOpen: controlledIsOpen,
  onClose,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // Handle ? key to toggle help
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Don't trigger if user is typing in an input
        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLSelectElement
        ) {
          return;
        }
        event.preventDefault();
        if (controlledIsOpen === undefined) {
          setInternalIsOpen(!internalIsOpen);
        } else {
          onClose?.();
        }
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        if (controlledIsOpen === undefined) {
          setInternalIsOpen(false);
        } else {
          onClose?.();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [internalIsOpen, isOpen, controlledIsOpen, onClose]);

  // Trap focus within modal when open
  useEffect(() => {
    if (!isOpen) return;

    const modal = document.querySelector('.keyboard-shortcuts-modal');
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey as any);
    firstElement?.focus();

    return () => {
      modal.removeEventListener('keydown', handleTabKey as any);
    };
  }, [isOpen]);

  const handleClose = () => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(false);
    } else {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  // Group shortcuts by category
  const categories = Array.from(new Set(SHORTCUTS.map(s => s.category)));

  return (
    <div
      className="keyboard-shortcuts-overlay"
      onClick={handleClose}
      data-testid="keyboard-shortcuts-overlay"
      role="presentation"
    >
      <div
        className="keyboard-shortcuts-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="shortcuts-title"
        aria-modal="true"
        data-testid="keyboard-shortcuts-modal"
      >
        <div className="modal-header">
          <h2 id="shortcuts-title">Keyboard Shortcuts</h2>
          <button
            className="close-button"
            onClick={handleClose}
            aria-label="Close keyboard shortcuts help"
            data-testid="close-button"
          >
            ✕
          </button>
        </div>

        <div className="modal-content">
          {categories.map(category => (
            <div key={category} className="shortcuts-category">
              <h3>{category}</h3>
              <dl className="shortcuts-list">
                {SHORTCUTS.filter(s => s.category === category).map(shortcut => (
                  <div key={shortcut.key} className="shortcut-item">
                    <dt className="shortcut-key">
                      <kbd>{shortcut.key}</kbd>
                    </dt>
                    <dd className="shortcut-description">{shortcut.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <p className="help-text">
            Press <kbd>?</kbd> to toggle this help or <kbd>Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};
